import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  UserPlus, Mail, Loader2, ClipboardList, RefreshCw, X, Trash2, CheckCircle, AlertTriangle,
  Clock, Send, BellRing
} from "lucide-react";
import { API, authFetch } from "@/App";

export function InviteUserDialog({ 
  open, 
  onOpenChange, 
  onSuccess,
  invitations = []
}) {
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    invite_type: "email",
    role: "community_member",
    tier: "basic",
    intake_message: ""
  });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [sendingIntake, setSendingIntake] = useState(false);
  const [triggeringFollowUp, setTriggeringFollowUp] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [createdUserCredentials, setCreatedUserCredentials] = useState(null);

  const handleSendInvite = async () => {
    if (!inviteForm.email) {
      toast.error("Email is required");
      return;
    }
    setSendingInvite(true);
    try {
      const response = await authFetch(`${API}/consultant/invite-user`, {
        method: "POST",
        body: JSON.stringify(inviteForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.temp_password) {
          setCreatedUserCredentials({
            email: data.email,
            password: data.temp_password,
            name: inviteForm.name
          });
          setShowCredentialsDialog(true);
        } else {
          toast.success(data.message);
        }
        
        resetForm();
        onSuccess?.();
        onOpenChange(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to send invite");
      }
    } catch (_err) {
      toast.error(`Unable to connect: ${_err.message}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSendIntake = async () => {
    if (!inviteForm.email) {
      toast.error("Email is required");
      return;
    }
    setSendingIntake(true);
    try {
      const response = await authFetch(`${API}/intake/send`, {
        method: "POST",
        body: JSON.stringify({
          client_email: inviteForm.email,
          client_name: inviteForm.name || "Potential Client",
          custom_message: inviteForm.intake_message || null
        })
      });
      
      if (response.ok) {
        toast.success(`Intake form sent to ${inviteForm.email}`);
        resetForm();
        onSuccess?.();
        onOpenChange(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to send intake form");
      }
    } catch (_err) {
      toast.error("Unable to connect. Please try again.");
    } finally {
      setSendingIntake(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await authFetch(`${API}/consultant/invitations/${inviteId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Invitation cancelled");
        onSuccess?.();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to cancel invitation");
      }
    } catch (_err) {
      toast.error("Unable to connect. Please try again.");
    }
  };

  const handleClearAllInvitations = async () => {
    if (!confirm("Are you sure you want to clear all pending invitations?")) {
      return;
    }
    try {
      const response = await authFetch(`${API}/consultant/invitations`, {
        method: "DELETE"
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "All invitations cleared");
        onSuccess?.();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to clear invitations");
      }
    } catch (_err) {
      toast.error("Unable to connect. Please try again.");
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const response = await authFetch(`${API}/consultant/invitations/${inviteId}/resend`, {
        method: "POST"
      });
      if (response.ok) {
        toast.success("Invitation resent");
        onSuccess?.();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to resend invitation");
      }
    } catch (_err) {
      toast.error("Unable to connect. Please try again.");
    }
  };

  const handleTriggerFollowUps = async () => {
    setTriggeringFollowUp(true);
    try {
      const response = await authFetch(`${API}/consultant/invitations/trigger-follow-ups`, {
        method: "POST"
      });
      if (response.ok) {
        toast.success("Follow-up emails sent to pending invitees");
        onSuccess?.();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to send follow-ups");
      }
    } catch (_err) {
      toast.error("Unable to connect. Please try again.");
    } finally {
      setTriggeringFollowUp(false);
    }
  };

  const getFollowUpBadge = (inv) => {
    const count = inv.follow_up_count || 0;
    if (inv.status === "accepted") return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Signed Up</Badge>;
    if (count >= 2) return <Badge className="bg-red-100 text-red-700 text-[10px]">Final Sent</Badge>;
    if (count === 1) return <Badge className="bg-amber-100 text-amber-700 text-[10px]">1 Follow-up</Badge>;
    return null;
  };

  const resetForm = () => {
    setInviteForm({
      email: "",
      name: "",
      invite_type: "email",
      role: "community_member",
      tier: "basic",
      intake_message: ""
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <UserPlus className="w-5 h-5 mr-2 text-green-600" />
              Invite User to Community
            </DialogTitle>
            <DialogDescription>
              Add users directly or send an email invitation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Invite Method</Label>
              <Select 
                value={inviteForm.invite_type} 
                onValueChange={(v) => setInviteForm({ ...inviteForm, invite_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Send Email Invitation</SelectItem>
                  <SelectItem value="direct">Create Account Directly</SelectItem>
                  <SelectItem value="intake">Send Intake Form First</SelectItem>
                </SelectContent>
              </Select>
              {inviteForm.invite_type === "intake" && (
                <p className="text-xs text-blue-600 mt-1">
                  Client will complete an intake form before getting dashboard access.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            
            {inviteForm.invite_type === "intake" && (
              <div className="space-y-2">
                <Label>Custom Message (Optional)</Label>
                <Textarea
                  value={inviteForm.intake_message}
                  onChange={(e) => setInviteForm({ ...inviteForm, intake_message: e.target.value })}
                  placeholder="Add a personalized message to include in the intake email..."
                  rows={2}
                />
              </div>
            )}
            
            {inviteForm.invite_type !== "intake" && (
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={inviteForm.role} 
                  onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="community_member">Community Member (Free)</SelectItem>
                    <SelectItem value="client">Client (Paid Plan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {inviteForm.role === "client" && inviteForm.invite_type !== "intake" && (
              <div className="space-y-2">
                <Label>Client Tier</Label>
                <Select 
                  value={inviteForm.tier} 
                  onValueChange={(v) => setInviteForm({ ...inviteForm, tier: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium_rapid">R.A.P.I.D.™</SelectItem>
                    <SelectItem value="podcast_academy">Podcast Academy</SelectItem>
                    <SelectItem value="growth_strategic">Strategic</SelectItem>
                    <SelectItem value="growth_optimization">Optimization</SelectItem>
                    <SelectItem value="growth_scale">Scale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <Button 
              onClick={inviteForm.invite_type === "intake" ? handleSendIntake : handleSendInvite}
              className="w-full bg-[#39FF14] hover:bg-[#32E010] text-black font-medium"
              disabled={(sendingInvite || sendingIntake) || !inviteForm.email}
              data-testid="send-invite-btn"
            >
              {(sendingInvite || sendingIntake) ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : inviteForm.invite_type === "intake" ? (
                <><ClipboardList className="w-4 h-4 mr-2" />Send Intake Form</>
              ) : inviteForm.invite_type === "direct" ? (
                <><UserPlus className="w-4 h-4 mr-2" />Create Account</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" />Send Invitation</>
              )}
            </Button>

            {invitations.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">Invitations ({invitations.length})</h4>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700 text-xs"
                      onClick={handleTriggerFollowUps}
                      disabled={triggeringFollowUp}
                      data-testid="trigger-follow-ups-btn"
                      title="Send follow-up emails to pending invitees"
                    >
                      {triggeringFollowUp ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <BellRing className="w-3 h-3 mr-1" />}
                      Follow Up
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 text-xs"
                      onClick={handleClearAllInvitations}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {invitations.map((inv) => {
                    const invId = inv.invitation_id || inv.invite_id;
                    return (
                    <div key={invId} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium truncate">{inv.email}</span>
                          <Badge variant="outline" className="text-[10px]">{inv.role || "member"}</Badge>
                          {inv.status === "pending" && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>}
                          {inv.status === "accepted" && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Accepted</Badge>}
                          {inv.status === "expired" && <Badge className="bg-neutral-100 text-neutral-500 text-[10px]">Expired</Badge>}
                          {getFollowUpBadge(inv)}
                        </div>
                        {(inv.follow_up_count > 0 || inv.last_follow_up_at) && (
                          <p className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1">
                            <Send className="w-2.5 h-2.5" />
                            {inv.follow_up_count || 0} follow-up{(inv.follow_up_count || 0) !== 1 ? "s" : ""} sent
                            {inv.last_follow_up_at && <> &middot; Last: {new Date(inv.last_follow_up_at).toLocaleDateString()}</>}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        {inv.status === "pending" && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => handleResendInvite(invId)}
                            title="Resend invitation"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 w-7 p-0 text-red-600"
                          onClick={() => handleCancelInvite(invId)}
                          title="Cancel invitation"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-600">
              <CheckCircle className="w-5 h-5 mr-2" />
              Account Created Successfully
            </DialogTitle>
            <DialogDescription>
              Share these credentials with the user. They should change their password after first login.
            </DialogDescription>
          </DialogHeader>
          {createdUserCredentials && (
            <div className="space-y-4 pt-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <p className="font-medium">{createdUserCredentials.name || "Not provided"}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <p className="font-medium">{createdUserCredentials.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Temporary Password</Label>
                  <div className="flex items-center space-x-2">
                    <code className="bg-white px-3 py-2 rounded border font-mono text-lg flex-1">
                      {createdUserCredentials.password}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(createdUserCredentials.password);
                        toast.success("Password copied to clipboard");
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-sm text-amber-600 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Please share these credentials securely with the user.
              </p>
              <Button 
                onClick={() => setShowCredentialsDialog(false)}
                className="w-full"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
